import { memo, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import MuiCircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Tooltip from '@mui/material/Tooltip';
import { keyframes } from '@mui/material';

// project imports
import useAuth from 'hooks/useAuth';
import useSubscriptions from 'hooks/useSubscriptions';
import PermissionGuard from 'components/permissions/PermissionGuard';
import { SystemFunction } from 'lib/permissions/constants';
import { useNavTheme } from 'contexts/NavThemeContext';
import { LOW_BALANCE_THRESHOLD } from 'components/billing/LowBalanceWarning';
import { useDispatch } from 'store';
import { cancelTask } from 'store/slices/tasks';
import { cancelInsightReport } from 'store/slices/insights';
import { endChatSession } from 'store/slices/chat';

// assets
import {
  IconAlertTriangle,
  IconArrowRight,
  IconPlus,
  IconBolt,
  IconZzz,
  IconX,
  IconGitPullRequest,
  IconClock,
  IconCoin,
  IconSquare,
  IconRobot,
  IconSparkles,
  IconMessageChatbot,
  IconHourglass,
} from '@tabler/icons-react';

// ==============================|| ANIMATIONS ||============================== //

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

// ==============================|| CUSTOM ICONS ||============================== //

// Jira icon SVG
function JiraIcon({ size = 18, color = '#0052CC' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        d="M11.53 2c0 2.4 1.97 4.35 4.35 4.35h1.78v1.7c0 2.4 1.94 4.34 4.34 4.35V2.84a.84.84 0 0 0-.84-.84H11.53Z"
        fill={color}
      />
      <path
        d="M6.77 6.8a4.36 4.36 0 0 0 4.34 4.38h1.8v1.7c0 2.4 1.93 4.35 4.33 4.37V7.65a.84.84 0 0 0-.84-.84H6.77Z"
        fill={color}
        opacity={0.85}
      />
      <path
        d="M2 11.6c0 2.4 1.95 4.34 4.35 4.35h1.78v1.72A4.35 4.35 0 0 0 12.46 22v-9.56a.84.84 0 0 0-.84-.84H2Z"
        fill={color}
        opacity={0.7}
      />
    </svg>
  );
}

// Asana icon SVG
function AsanaIcon({ size = 18, color = '#F06A6A' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="6.5" r="4.5" fill={color} />
      <circle cx="5.5" cy="16" r="4.5" fill={color} />
      <circle cx="18.5" cy="16" r="4.5" fill={color} />
    </svg>
  );
}

interface AgentTypeConfig {
  icon: React.ComponentType<{ size?: number; color?: string }> | typeof IconRobot;
  label: string;
  color: string;
}

const AGENT_TYPE_CONFIG: Record<string, AgentTypeConfig> = {
  jira: { icon: JiraIcon, label: 'Jira', color: '#0052CC' },
  asana: { icon: AsanaIcon, label: 'Asana', color: '#F06A6A' },
  pr_review: { icon: IconGitPullRequest, label: 'PR Review', color: '#8b5cf6' },
  insights: { icon: IconSparkles, label: 'Insights', color: '#f59e0b' },
  ticket_chat: { icon: IconMessageChatbot, label: 'Chat', color: '#6366f1' },
  default: { icon: IconRobot, label: 'Agent', color: '#6366f1' },
};

// ==============================|| RUNNING AGENTS MODAL ||============================== //

interface RunningAgent {
  id: string;
  type: 'jira' | 'asana' | 'pr_review' | 'insights' | 'ticket_chat';
  taskName: string;
  projectName: string;
  repoName?: string;
  externalId?: string;
  startedAt: string;
  acuUsed: number;
  cancelType: 'task' | 'insight' | 'pr_review' | 'chat_session';
  cancelId: string;
  projectId?: string;
  status?: 'active' | 'queued';
}

interface RunningAgentsModalProps {
  open: boolean;
  onClose: () => void;
  isLight: boolean;
  tokens: any;
  activeAgents: number;
  maxAgents: number;
  queuedAgents: number;
  agents: RunningAgent[];
  agentsLoading: boolean;
  onStop: (agent: RunningAgent) => void;
  onAgentClick: (agent: RunningAgent) => void;
  stoppingAgentId: string | null;
}

const AGENTS_PER_PAGE = 3;

function RunningAgentsModal({ open, onClose, isLight, tokens, activeAgents, maxAgents, queuedAgents, agents, agentsLoading, onStop, onAgentClick, stoppingAgentId }: RunningAgentsModalProps) {
  const [currentPage, setCurrentPage] = useState(0);
  const [confirmStopAgent, setConfirmStopAgent] = useState<RunningAgent | null>(null);

  const totalPages = Math.max(1, Math.ceil(agents.length / AGENTS_PER_PAGE));
  const startIdx = currentPage * AGENTS_PER_PAGE;
  const visibleAgents = agents.slice(startIdx, startIdx + AGENTS_PER_PAGE);
  const showPagination = agents.length > AGENTS_PER_PAGE;

  const formatDuration = (startedAt: string | Date) => {
    const start = typeof startedAt === 'string' ? new Date(startedAt) : startedAt;
    const diff = Date.now() - start.getTime();
    const mins = Math.floor(diff / 60000);
    const secs = Math.floor((diff % 60000) / 1000);
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          position: 'relative',
          background: isLight ? '#ffffff' : '#0f1419',
          border: `1px solid ${isLight ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)'}`,
          boxShadow: isLight
            ? '0 25px 50px -12px rgba(0, 0, 0, 0.15)'
            : '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          borderBottom: `1px solid ${isLight ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)'}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)',
            }}
          >
            <IconRobot size={20} style={{ color: '#ffffff' }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: '1.1rem',
                fontWeight: 700,
                fontFamily: '"Satoshi", sans-serif',
                color: tokens.textPrimary,
              }}
            >
              Running Agents
            </Typography>
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: tokens.textMuted,
              }}
            >
              {Math.min(activeAgents, maxAgents)} of {maxAgents} slots in use{queuedAgents > 0 ? ` \u00B7 ${queuedAgents} queued` : ''}
            </Typography>
          </Box>
        </Stack>
        <IconButton
          onClick={onClose}
          sx={{
            color: tokens.textMuted,
            '&:hover': { bgcolor: isLight ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.04)' },
          }}
        >
          <IconX size={20} />
        </IconButton>
      </Box>

      {/* Capacity Bar */}
      <Box sx={{ px: 3, py: 2, bgcolor: isLight ? 'rgba(0, 0, 0, 0.02)' : 'rgba(255, 255, 255, 0.02)' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: tokens.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Agent Capacity
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, fontFamily: '"JetBrains Mono", monospace', color: '#22c55e' }}>
              {Math.min(activeAgents, maxAgents)}/{maxAgents}
            </Typography>
            {queuedAgents > 0 && (
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, fontFamily: '"JetBrains Mono", monospace', color: '#f59e0b' }}>
                +{queuedAgents}
              </Typography>
            )}
          </Stack>
        </Stack>
        <Box sx={{ position: 'relative', height: 6, borderRadius: '3px', bgcolor: isLight ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.08)' }}>
          {/* Active agents bar (capped at 100%) */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              width: `${Math.min((activeAgents / maxAgents) * 100, 100)}%`,
              borderRadius: queuedAgents > 0 ? '3px 0 0 3px' : '3px',
              background: 'linear-gradient(90deg, #22c55e 0%, #16a34a 100%)',
              transition: 'width 0.3s ease',
              zIndex: 1,
            }}
          />
          {/* Queued agents bar (amber, shows after active) */}
          {queuedAgents > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: `${Math.min((activeAgents / maxAgents) * 100, 100)}%`,
                height: '100%',
                width: `${Math.min((queuedAgents / maxAgents) * 100, 100 - Math.min((activeAgents / maxAgents) * 100, 100))}%`,
                borderRadius: '0 3px 3px 0',
                background: 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)',
                opacity: 0.5,
                transition: 'width 0.3s ease',
              }}
            />
          )}
        </Box>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {/* Agent List */}
        <Stack spacing={0}>
          {visibleAgents.map((agent: any, index) => {
            const config = AGENT_TYPE_CONFIG[agent.type] || AGENT_TYPE_CONFIG.default;
            const AgentIcon = config.icon;
            const isQueued = agent.status === 'queued';
            // Show repoName for PR review, projectName for others
            const contextLabel = agent.type === 'pr_review' ? agent.repoName : agent.projectName;

            return (
              <Box
                key={agent.id}
                onClick={() => onAgentClick(agent)}
                sx={{
                  px: 3,
                  py: 2,
                  cursor: 'pointer',
                  borderBottom: index < visibleAgents.length - 1
                    ? `1px solid ${isLight ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.04)'}`
                    : 'none',
                  transition: 'background 0.15s ease',
                  opacity: isQueued ? 0.75 : 1,
                  '&:hover': {
                    bgcolor: isLight ? 'rgba(0, 0, 0, 0.02)' : 'rgba(255, 255, 255, 0.02)',
                  },
                }}
              >
                <Stack direction="row" alignItems="flex-start" spacing={2}>
                  {/* Agent Type Icon */}
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '10px',
                      background: isLight ? `${config.color}12` : `${config.color}20`,
                      border: `1px solid ${config.color}30`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <AgentIcon size={18} color={config.color} />
                  </Box>

                  {/* Agent Details */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                      <Chip
                        label={config.label}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.6rem',
                          fontWeight: 700,
                          fontFamily: '"Satoshi", sans-serif',
                          bgcolor: isLight ? `${config.color}15` : `${config.color}25`,
                          color: config.color,
                          border: `1px solid ${config.color}30`,
                          '& .MuiChip-label': { px: 0.75 },
                        }}
                      />
                      {isQueued && (
                        <Chip
                          label="Queued"
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.55rem',
                            fontWeight: 700,
                            fontFamily: '"Satoshi", sans-serif',
                            bgcolor: isLight ? 'rgba(245, 158, 11, 0.1)' : 'rgba(245, 158, 11, 0.15)',
                            color: '#f59e0b',
                            border: '1px solid rgba(245, 158, 11, 0.3)',
                            '& .MuiChip-label': { px: 0.75 },
                          }}
                        />
                      )}
                      {contextLabel && (
                        <Typography
                          sx={{
                            fontSize: '0.7rem',
                            color: tokens.textMuted,
                            fontFamily: agent.type === 'pr_review' ? '"JetBrains Mono", monospace' : '"Satoshi", sans-serif',
                          }}
                        >
                          {contextLabel}
                        </Typography>
                      )}
                    </Stack>

                    <Typography
                      sx={{
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        color: tokens.textPrimary,
                        fontFamily: '"Satoshi", sans-serif',
                        mb: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {agent.taskName}
                    </Typography>

                    {isQueued ? (
                      /* Queued agent: waiting indicator instead of progress bar */
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                        <IconHourglass size={12} style={{ color: '#f59e0b' }} />
                        <Typography
                          sx={{
                            fontSize: '0.7rem',
                            fontFamily: '"Inter", sans-serif',
                            color: '#f59e0b',
                            fontWeight: 500,
                          }}
                        >
                          Waiting for slot...
                        </Typography>
                      </Stack>
                    ) : (
                      <>
                        {/* Progress indicator */}
                        <Box sx={{ mb: 1.5 }}>
                          <LinearProgress
                            variant="indeterminate"
                            sx={{
                              height: 3,
                              borderRadius: '2px',
                              bgcolor: isLight ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.04)',
                              '& .MuiLinearProgress-bar': {
                                borderRadius: '2px',
                                background: `linear-gradient(90deg, ${config.color} 0%, ${config.color}80 100%)`,
                                animation: `${shimmer} 1.5s ease-in-out infinite`,
                                backgroundSize: '200% 100%',
                              },
                            }}
                          />
                        </Box>

                        {/* Stats Row */}
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <IconClock size={12} style={{ color: tokens.textDimmed }} />
                            <Typography
                              sx={{
                                fontSize: '0.7rem',
                                fontFamily: '"JetBrains Mono", monospace',
                                fontWeight: 600,
                                color: tokens.textMuted,
                                animation: `${pulse} 2s ease-in-out infinite`,
                              }}
                            >
                              {formatDuration(agent.startedAt)}
                            </Typography>
                          </Stack>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <IconCoin size={12} style={{ color: '#22c55e' }} />
                            <Typography
                              sx={{
                                fontSize: '0.7rem',
                                fontFamily: '"JetBrains Mono", monospace',
                                fontWeight: 600,
                                color: '#22c55e',
                              }}
                            >
                              {Math.round(Number(agent.acuUsed))} ACU
                            </Typography>
                          </Stack>
                        </Stack>
                      </>
                    )}
                  </Box>

                  {/* Stop Button — only for active agents */}
                  {!isQueued && (
                    <ButtonBase
                      onClick={(e) => { e.stopPropagation(); setConfirmStopAgent(agent); }}
                      disabled={stoppingAgentId === agent.id}
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: isLight ? 'rgba(239, 68, 68, 0.08)' : 'rgba(239, 68, 68, 0.12)',
                        border: `1px solid ${isLight ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.25)'}`,
                        transition: 'all 0.15s ease',
                        opacity: stoppingAgentId === agent.id ? 0.5 : 1,
                        '&:hover': {
                          bgcolor: isLight ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.2)',
                          border: `1px solid ${isLight ? 'rgba(239, 68, 68, 0.35)' : 'rgba(239, 68, 68, 0.4)'}`,
                          transform: 'scale(1.05)',
                        },
                        '&.Mui-disabled': {
                          opacity: 0.5,
                        },
                      }}
                    >
                      {stoppingAgentId === agent.id ? (
                        <MuiCircularProgress size={14} sx={{ color: '#ef4444' }} />
                      ) : (
                        <IconSquare size={14} fill="#ef4444" style={{ color: '#ef4444' }} />
                      )}
                    </ButtonBase>
                  )}
                </Stack>
              </Box>
            );
          })}
        </Stack>

        {/* Pagination Controls */}
        {showPagination && (
          <Box
            sx={{
              px: 3,
              py: 2,
              borderTop: `1px solid ${isLight ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)'}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              bgcolor: isLight ? 'rgba(0, 0, 0, 0.02)' : 'rgba(255, 255, 255, 0.02)',
            }}
          >
            {/* Previous Button */}
            <IconButton
              size="small"
              disabled={currentPage === 0}
              onClick={() => setCurrentPage((p) => p - 1)}
              sx={{
                width: 32,
                height: 32,
                borderRadius: '8px',
                bgcolor: isLight ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.06)',
                border: `1px solid ${isLight ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.1)'}`,
                color: tokens.textSecondary,
                transition: 'all 0.15s ease',
                '&:hover': {
                  bgcolor: isLight ? 'rgba(99, 102, 241, 0.1)' : 'rgba(99, 102, 241, 0.2)',
                  border: `1px solid ${isLight ? 'rgba(99, 102, 241, 0.3)' : 'rgba(99, 102, 241, 0.4)'}`,
                  color: '#6366f1',
                },
                '&.Mui-disabled': {
                  bgcolor: 'transparent',
                  border: `1px solid ${isLight ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.04)'}`,
                  color: tokens.textDimmed,
                  opacity: 0.5,
                },
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </IconButton>

            {/* Page Indicator */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.75,
                borderRadius: '8px',
                bgcolor: isLight ? 'rgba(99, 102, 241, 0.08)' : 'rgba(99, 102, 241, 0.15)',
                border: `1px solid ${isLight ? 'rgba(99, 102, 241, 0.15)' : 'rgba(99, 102, 241, 0.25)'}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  fontFamily: '"JetBrains Mono", monospace',
                  color: '#6366f1',
                }}
              >
                {currentPage + 1}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  color: tokens.textMuted,
                }}
              >
                /
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  fontFamily: '"JetBrains Mono", monospace',
                  color: tokens.textSecondary,
                }}
              >
                {totalPages}
              </Typography>
            </Box>

            {/* Next Button */}
            <IconButton
              size="small"
              disabled={currentPage >= totalPages - 1}
              onClick={() => setCurrentPage((p) => p + 1)}
              sx={{
                width: 32,
                height: 32,
                borderRadius: '8px',
                bgcolor: isLight ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.06)',
                border: `1px solid ${isLight ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.1)'}`,
                color: tokens.textSecondary,
                transition: 'all 0.15s ease',
                '&:hover': {
                  bgcolor: isLight ? 'rgba(99, 102, 241, 0.1)' : 'rgba(99, 102, 241, 0.2)',
                  border: `1px solid ${isLight ? 'rgba(99, 102, 241, 0.3)' : 'rgba(99, 102, 241, 0.4)'}`,
                  color: '#6366f1',
                },
                '&.Mui-disabled': {
                  bgcolor: 'transparent',
                  border: `1px solid ${isLight ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.04)'}`,
                  color: tokens.textDimmed,
                  opacity: 0.5,
                },
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </IconButton>
          </Box>
        )}

        {/* Confirmation Dialog for Stop */}
        {confirmStopAgent && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: isLight ? 'rgba(255, 255, 255, 0.95)' : 'rgba(15, 20, 25, 0.95)',
              zIndex: 10,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 3,
              borderRadius: '16px',
            }}
          >
            <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: 'rgba(239, 68, 68, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
              <IconSquare size={20} fill="#ef4444" style={{ color: '#ef4444' }} />
            </Box>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, fontFamily: '"Satoshi", sans-serif', color: tokens.textPrimary, mb: 0.5, textAlign: 'center' }}>
              Stop this agent?
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', fontFamily: '"Inter", sans-serif', color: tokens.textMuted, mb: 2.5, textAlign: 'center', maxWidth: 280, lineHeight: 1.5 }}>
              The agent will finish its current step, create a WIP PR if there are code changes, and stop.
            </Typography>
            <Stack direction="row" spacing={1.5}>
              <ButtonBase
                onClick={() => setConfirmStopAgent(null)}
                sx={{
                  px: 2.5, py: 0.875, borderRadius: '8px',
                  bgcolor: isLight ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.06)',
                  border: `1px solid ${isLight ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.1)'}`,
                  fontSize: '0.8rem', fontWeight: 600, fontFamily: '"Satoshi", sans-serif', color: tokens.textSecondary,
                }}
              >
                Cancel
              </ButtonBase>
              <ButtonBase
                onClick={() => {
                  onStop(confirmStopAgent);
                  setConfirmStopAgent(null);
                }}
                sx={{
                  px: 2.5, py: 0.875, borderRadius: '8px',
                  bgcolor: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.25)',
                  fontSize: '0.8rem', fontWeight: 600, fontFamily: '"Satoshi", sans-serif', color: '#ef4444',
                  '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.15)' },
                }}
              >
                Stop Agent
              </ButtonBase>
            </Stack>
          </Box>
        )}

        {/* Loading State */}
        {agentsLoading && agents.length === 0 && (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <MuiCircularProgress size={28} sx={{ color: '#22c55e', mb: 2 }} />
            <Typography sx={{ fontSize: '0.85rem', fontFamily: '"Inter", sans-serif', color: tokens.textMuted }}>
              Loading agents...
            </Typography>
          </Box>
        )}

        {/* Empty State (when no agents running or queued) */}
        {!agentsLoading && agents.length === 0 && (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                bgcolor: isLight ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.04)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <IconRobot size={24} style={{ color: tokens.textDimmed }} />
            </Box>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, fontFamily: '"Satoshi", sans-serif', color: tokens.textSecondary, mb: 0.5 }}>
              No agents running
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', fontFamily: '"Inter", sans-serif', color: tokens.textMuted }}>
              All agent slots are available
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ==============================|| CIRCULAR PROGRESS RING ||============================== //

interface CircularProgressProps {
  value: number;
  size?: number;
  strokeWidth?: number;
  isLight: boolean;
  showLabel?: boolean;
}

function CircularProgress({ value, size = 56, strokeWidth = 4, isLight, showLabel = true }: CircularProgressProps) {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (value / 100) * circumference;

  // Color based on usage level
  const getProgressColor = () => {
    if (value > 90) return '#ef4444';
    if (value > 70) return '#f59e0b';
    return '#6366f1';
  };

  const progressColor = getProgressColor();
  const trackColor = isLight ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.08)';

  return (
    <Box sx={{ position: 'relative', width: size, height: size }}>
      <svg
        width={size}
        height={size}
        style={{ transform: 'rotate(-90deg)' }}
      >
        {/* Background track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={trackColor}
          strokeWidth={strokeWidth}
        />
        {/* Progress arc */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={progressColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{
            transition: 'stroke-dashoffset 0.6s cubic-bezier(0.4, 0, 0.2, 1), stroke 0.3s ease',
          }}
        />
      </svg>
      {/* Center percentage */}
      {showLabel && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            sx={{
              fontSize: size < 40 ? '0.6rem' : '0.8rem',
              fontWeight: 700,
              fontFamily: '"JetBrains Mono", monospace',
              color: progressColor,
              lineHeight: 1,
            }}
          >
            {Math.round(value)}%
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// ==============================|| COLLAPSED MENU CARD (72px width) ||============================== //

interface CollapsedMenuCardProps {
  usagePercentage: number;
  activeAgents: number;
  maxAgents: number;
  queuedAgents: number;
  isLight: boolean;
  tokens: any;
  isLowBalance: boolean;
  isScheduledForCancellation: boolean;
  onClick: () => void;
  onCancellationClick: () => void;
  onAgentsClick: () => void;
}

function CollapsedMenuCard({
  usagePercentage,
  activeAgents,
  maxAgents,
  queuedAgents,
  isLight,
  tokens,
  isLowBalance,
  isScheduledForCancellation,
  onClick,
  onCancellationClick,
  onAgentsClick
}: CollapsedMenuCardProps) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {/* Low Balance Warning - Collapsed */}
      {isLowBalance && (
        <Box
          onClick={onClick}
          sx={{
            mx: 1,
            p: 1,
            borderRadius: '10px',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5,
            background: isLight
              ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.08) 0%, rgba(220, 38, 38, 0.12) 100%)'
              : 'linear-gradient(135deg, rgba(239, 68, 68, 0.12) 0%, rgba(185, 28, 28, 0.18) 100%)',
            border: '1px solid rgba(239, 68, 68, 0.25)',
            transition: 'all 0.2s ease',
            '&:hover': {
              background: isLight
                ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.12) 0%, rgba(220, 38, 38, 0.16) 100%)'
                : 'linear-gradient(135deg, rgba(239, 68, 68, 0.16) 0%, rgba(185, 28, 28, 0.22) 100%)',
              border: '1px solid rgba(239, 68, 68, 0.35)',
              transform: 'translateY(-1px)',
            },
            animation: 'lowBalancePulse 3s ease-in-out infinite',
            '@keyframes lowBalancePulse': {
              '0%, 100%': { boxShadow: '0 0 0 0 rgba(239, 68, 68, 0)' },
              '50%': { boxShadow: '0 0 12px -2px rgba(239, 68, 68, 0.2)' },
            },
          }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(239, 68, 68, 0.15)',
            }}
          >
            <IconZzz size={14} style={{ color: '#ef4444' }} />
          </Box>
          <Typography
            sx={{
              fontSize: '0.55rem',
              fontWeight: 700,
              color: '#ef4444',
              textAlign: 'center',
              lineHeight: 1.2,
            }}
          >
            Paused
          </Typography>
        </Box>
      )}

      {/* Main Stats Card - Collapsed */}
      <Box
        onClick={onClick}
        sx={{
          mx: 1,
          mb: isScheduledForCancellation ? 0 : 1.5,
          p: 1,
          borderRadius: '12px',
          position: 'relative',
          overflow: 'hidden',
          cursor: 'pointer',
          background: isLight ? '#ffffff' : 'rgba(255, 255, 255, 0.02)',
          border: `1px solid ${isLight ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)'}`,
          transition: 'all 0.2s ease',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          '&:hover': {
            border: `1px solid ${isLight ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)'}`,
            transform: 'translateY(-1px)',
          },
        }}
      >
        {/* Circular Progress - smaller for collapsed */}
        <CircularProgress
          value={usagePercentage}
          size={38}
          strokeWidth={3}
          isLight={isLight}
          showLabel={true}
        />

        {/* Agent indicator - compact, opens Running Agents modal */}
        <Box
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onAgentsClick();
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 0.75,
            py: 0.25,
            borderRadius: '6px',
            background: isLight ? 'rgba(0, 0, 0, 0.03)' : 'rgba(255, 255, 255, 0.04)',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
            position: 'relative',
            '&:hover': {
              background: isLight ? 'rgba(34, 197, 94, 0.08)' : 'rgba(34, 197, 94, 0.12)',
            },
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: activeAgents > 0 ? '#22c55e' : tokens.textDimmed,
              boxShadow: activeAgents > 0 ? '0 0 6px rgba(34, 197, 94, 0.4)' : 'none',
              animation: activeAgents > 0 ? 'pulse 2s ease-in-out infinite' : 'none',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.6 },
              },
            }}
          />
          <Typography
            sx={{
              fontSize: '0.6rem',
              fontWeight: 600,
              fontFamily: '"JetBrains Mono", monospace',
              color: activeAgents > 0 ? '#22c55e' : tokens.textDimmed,
            }}
          >
            {Math.min(activeAgents, maxAgents)}/{maxAgents}
          </Typography>
          {queuedAgents > 0 && (
            <Typography
              sx={{
                fontSize: '0.5rem',
                fontWeight: 700,
                fontFamily: '"JetBrains Mono", monospace',
                color: '#f59e0b',
                lineHeight: 1,
              }}
            >
              +{queuedAgents}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Subscription Ending Warning - Collapsed */}
      {isScheduledForCancellation && (
        <Box
          onClick={onCancellationClick}
          sx={{
            mx: 1,
            mb: 1.5,
            p: 1,
            borderRadius: '10px',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5,
            background: isLight ? 'rgba(245, 158, 11, 0.06)' : 'rgba(245, 158, 11, 0.08)',
            border: '1px solid rgba(245, 158, 11, 0.2)',
            transition: 'all 0.2s ease',
            '&:hover': {
              background: isLight ? 'rgba(245, 158, 11, 0.1)' : 'rgba(245, 158, 11, 0.12)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              transform: 'translateY(-1px)',
            },
          }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: isLight ? 'rgba(245, 158, 11, 0.1)' : 'rgba(245, 158, 11, 0.12)',
              border: '1px solid rgba(245, 158, 11, 0.2)',
            }}
          >
            <IconAlertTriangle size={14} style={{ color: '#f59e0b' }} />
          </Box>
          <Typography
            sx={{
              fontSize: '0.55rem',
              fontWeight: 700,
              color: '#f59e0b',
              textAlign: 'center',
              lineHeight: 1.2,
            }}
          >
            Ending
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// ==============================|| SIDEBAR - MENU CARD ||============================== //

interface MenuCardProps {
  isCollapsed?: boolean;
}

function MenuCard({ isCollapsed = false }: MenuCardProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { subscription, plan, currentBalance, acuUsed, agentCapacity, runningAgents, runningAgentsStatus, fetchSubscription, fetchUsage, fetchRunningAgents, isSuccess } = useSubscriptions();
  const { user } = useAuth();
  const { mode, tokens } = useNavTheme();
  const isLight = mode === 'light';
  const [agentsModalOpen, setAgentsModalOpen] = useState(false);
  const [stoppingAgentId, setStoppingAgentId] = useState<string | null>(null);

  const activeAgents = agentCapacity?.activeAgents || 0;
  const maxAgents = agentCapacity?.maxConcurrentAgents || 0;
  const queuedAgents = agentCapacity?.queuedAgents || 0;

  const handleBuyMoreClick = () => {
    if (!plan || plan.code === 'free') {
      navigate('/settings?tab=subscription');
    } else {
      navigate('/settings?tab=buy-acu');
    }
  };

  const handleAgentsClick = () => {
    setAgentsModalOpen(true);
    fetchRunningAgents();
  };

  const handleAgentClick = useCallback((agent: RunningAgent) => {
    if (!agent.projectId) return;
    setAgentsModalOpen(false);
    if (agent.type === 'ticket_chat') {
      navigate(`/project-agents/${agent.projectId}?tab=ticket-management&subtab=chat`);
    } else if (agent.type === 'insights') {
      navigate(`/project-agents/${agent.projectId}?tab=insights`);
    } else {
      navigate(`/project-agents/${agent.projectId}?tab=agent-work`);
    }
  }, [navigate]);

  const handleStopAgent = useCallback(async (agent: RunningAgent) => {
    setStoppingAgentId(agent.id);
    try {
      if (agent.cancelType === 'task') {
        await dispatch(cancelTask(agent.cancelId)).unwrap();
      } else if (agent.cancelType === 'insight' && agent.projectId) {
        await dispatch(cancelInsightReport({ projectId: agent.projectId, reportId: agent.cancelId })).unwrap();
      } else if (agent.cancelType === 'chat_session' && agent.projectId) {
        await dispatch(endChatSession({ projectId: agent.projectId, sessionId: agent.cancelId })).unwrap();
      }
      // Refresh running agents list and sidebar capacity after cancel
      fetchRunningAgents();
      fetchSubscription();
      fetchUsage();
    } catch (err) {
      // Error handled by slice
    } finally {
      setStoppingAgentId(null);
    }
  }, [dispatch, fetchRunningAgents, fetchSubscription, fetchUsage]);

  useEffect(() => {
    if (user?.organizationId) {
      fetchSubscription();
      fetchUsage();
    }
  }, [user?.organizationId, fetchSubscription, fetchUsage]);

  // Periodic refresh while agents are running or queued — catches ACU from all worker types
  useEffect(() => {
    if (!user?.organizationId || (activeAgents === 0 && queuedAgents === 0)) return;

    const interval = setInterval(() => {
      fetchSubscription();
      fetchUsage();
      fetchRunningAgents();
    }, 30_000);

    return () => clearInterval(interval);
  }, [user?.organizationId, activeAgents, queuedAgents, fetchSubscription, fetchUsage, fetchRunningAgents]);

  // currentBalance is the REMAINING balance (after deductions), not the total
  // To get total: remaining + used = total
  const totalAvailable = currentBalance + acuUsed;
  const usagePercentage = totalAvailable > 0 ? (acuUsed / totalAvailable) * 100 : 0;
  const remaining = currentBalance; // currentBalance IS the remaining balance
  const isScheduledForCancellation = subscription?.cancelAtPeriodEnd && subscription?.renewalDate;
  const isLowBalance = isSuccess && remaining < LOW_BALANCE_THRESHOLD;
  const isFreePlan = !plan || plan.code === 'free';

  // Render collapsed version
  if (isCollapsed) {
    return (
      <>
        <CollapsedMenuCard
          usagePercentage={usagePercentage}
          activeAgents={agentCapacity?.activeAgents || 0}
          maxAgents={agentCapacity?.maxConcurrentAgents || 0}
          queuedAgents={queuedAgents}
          isLight={isLight}
          tokens={tokens}
          isLowBalance={isLowBalance}
          isScheduledForCancellation={!!isScheduledForCancellation}
          onClick={handleBuyMoreClick}
          onCancellationClick={() => navigate('/settings?tab=subscription')}
          onAgentsClick={handleAgentsClick}
        />
        <RunningAgentsModal
          open={agentsModalOpen}
          onClose={() => setAgentsModalOpen(false)}
          isLight={isLight}
          tokens={tokens}
          activeAgents={activeAgents}
          maxAgents={maxAgents}
          queuedAgents={queuedAgents}
          agents={runningAgents || []}
          agentsLoading={runningAgentsStatus === 'loading'}
          onStop={handleStopAgent}
          onAgentClick={handleAgentClick}
          stoppingAgentId={stoppingAgentId}
        />
      </>
    );
  }

  return (
    <>
      {/* Low Balance Critical Alert - Shows when tasks won't execute */}
      {isLowBalance && (
        <Box
          onClick={handleBuyMoreClick}
          sx={{
            mx: 1.5,
            mb: 2,
            borderRadius: '12px',
            cursor: 'pointer',
            position: 'relative',
            // Critical red theme with subtle animation
            background: isLight
              ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.08) 0%, rgba(220, 38, 38, 0.12) 100%)'
              : 'linear-gradient(135deg, rgba(239, 68, 68, 0.12) 0%, rgba(185, 28, 28, 0.18) 100%)',
            border: '1px solid rgba(239, 68, 68, 0.25)',
            transition: 'all 0.2s ease',
            '&:hover': {
              background: isLight
                ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.12) 0%, rgba(220, 38, 38, 0.16) 100%)'
                : 'linear-gradient(135deg, rgba(239, 68, 68, 0.16) 0%, rgba(185, 28, 28, 0.22) 100%)',
              border: '1px solid rgba(239, 68, 68, 0.35)',
              transform: 'translateY(-1px)',
            },
            // Subtle pulse animation for attention
            animation: 'lowBalancePulse 3s ease-in-out infinite',
            '@keyframes lowBalancePulse': {
              '0%, 100%': { boxShadow: '0 0 0 0 rgba(239, 68, 68, 0)' },
              '50%': { boxShadow: '0 0 12px -2px rgba(239, 68, 68, 0.2)' },
            },
          }}
        >
          {/* Header section - stacked layout for narrow sidebar */}
          <Box sx={{ p: 1.5 }}>
            {/* Title row with icon */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(239, 68, 68, 0.15)',
                  flexShrink: 0,
                }}
              >
                <IconZzz size={14} style={{ color: '#ef4444' }} />
              </Box>
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  color: '#ef4444',
                  fontFamily: '"Satoshi", sans-serif',
                }}
              >
                Tasks Paused
              </Typography>
            </Box>
            {/* Description - full width for proper wrapping */}
            <Typography
              component="div"
              sx={{
                fontSize: '0.7rem',
                color: tokens.textSecondary,
                lineHeight: 1.6,
                width: '100%',
                wordWrap: 'break-word',
                whiteSpace: 'normal',
              }}
            >
              Balance below{' '}
              <Box
                component="span"
                sx={{
                  fontWeight: 700,
                  color: '#ef4444',
                  fontFamily: '"JetBrains Mono", monospace',
                }}
              >
                {LOW_BALANCE_THRESHOLD} ACU
              </Box>
              . No tasks will run until you top up.
            </Typography>
          </Box>

          {/* CTA Footer */}
          <Box
            sx={{
              px: 1.75,
              py: 1.25,
              background: 'rgba(239, 68, 68, 0.08)',
              borderTop: '1px solid rgba(239, 68, 68, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <IconBolt size={14} style={{ color: '#dc2626' }} />
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  color: '#dc2626',
                  fontFamily: '"Satoshi", sans-serif',
                }}
              >
                {isFreePlan ? 'Upgrade Plan' : 'Top Up Now'}
              </Typography>
            </Box>
            <IconArrowRight size={14} style={{ color: '#dc2626' }} />
          </Box>
        </Box>
      )}

      {/* AI Compute Units Card - Circular Progress Design */}
      <Box
        sx={{
          mx: 1.5,
          mb: 2,
          p: 2,
          borderRadius: '12px',
          position: 'relative',
          overflow: 'hidden',
          background: isLight ? '#ffffff' : 'rgba(255, 255, 255, 0.02)',
          border: `1px solid ${isLight ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)'}`,
          transition: 'all 0.2s ease',
          '&:hover': {
            border: `1px solid ${isLight ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)'}`,
          },
        }}
      >
        {/* Main content row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Circular Progress */}
          <CircularProgress value={usagePercentage} isLight={isLight} />

          {/* Stats */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '0.65rem',
                fontWeight: 500,
                color: tokens.textDimmed,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                mb: 0.5,
              }}
            >
              AI Compute Units (ACU)
            </Typography>

            {/* Used / Total */}
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 0.25 }}>
              <Typography
                sx={{
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  fontFamily: '"JetBrains Mono", monospace',
                  color: tokens.textPrimary,
                  lineHeight: 1,
                }}
              >
                {acuUsed.toLocaleString()}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  color: tokens.textDimmed,
                }}
              >
                / {totalAvailable.toLocaleString()}
              </Typography>
            </Box>

            {/* Remaining label */}
            <Typography
              sx={{
                fontSize: '0.65rem',
                color: tokens.textDimmed,
              }}
            >
              {remaining.toLocaleString()} remaining
            </Typography>
          </Box>
        </Box>

        {/* Agent Capacity Indicator - Always clickable */}
        {agentCapacity && (
          <Tooltip
            arrow
            placement="right"
            slotProps={{
              tooltip: {
                sx: {
                  bgcolor: isLight ? '#ffffff' : '#1a1f2e',
                  color: isLight ? '#334155' : 'rgba(255, 255, 255, 0.85)',
                  border: `1px solid ${isLight ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.1)'}`,
                  boxShadow: isLight
                    ? '0 8px 24px rgba(0, 0, 0, 0.12)'
                    : '0 8px 24px rgba(0, 0, 0, 0.4)',
                  borderRadius: '10px',
                  p: 0,
                  overflow: 'hidden',
                  minWidth: 160,
                },
              },
              arrow: {
                sx: {
                  color: isLight ? '#ffffff' : '#1a1f2e',
                  '&::before': {
                    border: `1px solid ${isLight ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.1)'}`,
                  },
                },
              },
            }}
            title={
              <Box>
                {/* Active Section */}
                <Box sx={{ px: 1.5, pt: 1.25, pb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#22c55e', boxShadow: '0 0 6px rgba(34, 197, 94, 0.4)' }} />
                      <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: isLight ? '#334155' : 'rgba(255, 255, 255, 0.7)' }}>
                        Active
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, fontFamily: '"JetBrains Mono", monospace', color: '#22c55e' }}>
                      {Math.min(activeAgents, maxAgents)}/{maxAgents}
                    </Typography>
                  </Box>
                  {agentCapacity.breakdown && (
                    <Stack spacing={0.25} sx={{ pl: 1.75 }}>
                      {(agentCapacity.breakdown.activeTasks > 0) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography sx={{ fontSize: '0.65rem', color: isLight ? '#64748b' : 'rgba(255, 255, 255, 0.5)' }}>Jira/Asana</Typography>
                          <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, fontFamily: '"JetBrains Mono", monospace', color: isLight ? '#475569' : 'rgba(255, 255, 255, 0.65)' }}>{agentCapacity.breakdown.activeTasks}</Typography>
                        </Box>
                      )}
                      {(agentCapacity.breakdown.activeReviews > 0) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography sx={{ fontSize: '0.65rem', color: isLight ? '#64748b' : 'rgba(255, 255, 255, 0.5)' }}>PR Review</Typography>
                          <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, fontFamily: '"JetBrains Mono", monospace', color: isLight ? '#475569' : 'rgba(255, 255, 255, 0.65)' }}>{agentCapacity.breakdown.activeReviews}</Typography>
                        </Box>
                      )}
                      {(agentCapacity.breakdown.activeInsights > 0) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography sx={{ fontSize: '0.65rem', color: isLight ? '#64748b' : 'rgba(255, 255, 255, 0.5)' }}>Insights</Typography>
                          <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, fontFamily: '"JetBrains Mono", monospace', color: isLight ? '#475569' : 'rgba(255, 255, 255, 0.65)' }}>{agentCapacity.breakdown.activeInsights}</Typography>
                        </Box>
                      )}
                      {(agentCapacity.breakdown.activeChats > 0) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography sx={{ fontSize: '0.65rem', color: isLight ? '#64748b' : 'rgba(255, 255, 255, 0.5)' }}>Chat</Typography>
                          <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, fontFamily: '"JetBrains Mono", monospace', color: isLight ? '#475569' : 'rgba(255, 255, 255, 0.65)' }}>{agentCapacity.breakdown.activeChats}</Typography>
                        </Box>
                      )}
                    </Stack>
                  )}
                </Box>
                {/* Queued Section */}
                {queuedAgents > 0 && (
                  <Box sx={{
                    px: 1.5, pt: 1, pb: 1.25,
                    borderTop: `1px solid ${isLight ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)'}`,
                    bgcolor: isLight ? 'rgba(245, 158, 11, 0.04)' : 'rgba(245, 158, 11, 0.06)',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <IconHourglass size={10} style={{ color: '#f59e0b' }} />
                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#f59e0b' }}>
                          Queued
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, fontFamily: '"JetBrains Mono", monospace', color: '#f59e0b' }}>
                        {queuedAgents}
                      </Typography>
                    </Box>
                    {agentCapacity.queuedBreakdown && (
                      <Stack spacing={0.25} sx={{ pl: 1.75 }}>
                        {(agentCapacity.queuedBreakdown.queuedTasks > 0) && (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontSize: '0.65rem', color: isLight ? '#92400e' : 'rgba(251, 191, 36, 0.6)' }}>Jira/Asana</Typography>
                            <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, fontFamily: '"JetBrains Mono", monospace', color: isLight ? '#92400e' : 'rgba(251, 191, 36, 0.7)' }}>{agentCapacity.queuedBreakdown.queuedTasks}</Typography>
                          </Box>
                        )}
                        {(agentCapacity.queuedBreakdown.queuedReviews > 0) && (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontSize: '0.65rem', color: isLight ? '#92400e' : 'rgba(251, 191, 36, 0.6)' }}>PR Review</Typography>
                            <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, fontFamily: '"JetBrains Mono", monospace', color: isLight ? '#92400e' : 'rgba(251, 191, 36, 0.7)' }}>{agentCapacity.queuedBreakdown.queuedReviews}</Typography>
                          </Box>
                        )}
                        {(agentCapacity.queuedBreakdown.queuedInsights > 0) && (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontSize: '0.65rem', color: isLight ? '#92400e' : 'rgba(251, 191, 36, 0.6)' }}>Insights</Typography>
                            <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, fontFamily: '"JetBrains Mono", monospace', color: isLight ? '#92400e' : 'rgba(251, 191, 36, 0.7)' }}>{agentCapacity.queuedBreakdown.queuedInsights}</Typography>
                          </Box>
                        )}
                      </Stack>
                    )}
                  </Box>
                )}
              </Box>
            }
          >
            <Box
              onClick={handleAgentsClick}
              sx={{
                mt: 1.5,
                pt: 1.5,
                mx: -2,
                px: 2,
                pb: 1.5,
                borderTop: `1px solid ${isLight ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.04)'}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                borderRadius: '0 0 10px 10px',
                transition: 'all 0.15s ease',
                '&:hover': {
                  bgcolor: isLight
                    ? activeAgents > 0 ? 'rgba(34, 197, 94, 0.04)' : 'rgba(0, 0, 0, 0.02)'
                    : activeAgents > 0 ? 'rgba(34, 197, 94, 0.08)' : 'rgba(255, 255, 255, 0.03)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: activeAgents > 0 ? '#22c55e' : tokens.textDimmed,
                    boxShadow: activeAgents > 0 ? '0 0 8px rgba(34, 197, 94, 0.4)' : 'none',
                    animation: activeAgents > 0 ? 'agentPulse 2s ease-in-out infinite' : 'none',
                    '@keyframes agentPulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.6 },
                    },
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '0.65rem',
                    fontWeight: 500,
                    color: tokens.textDimmed,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Agents
                </Typography>
              </Box>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <Typography
                  sx={{
                    fontSize: '0.6rem',
                    fontWeight: 600,
                    color: activeAgents > 0 ? '#22c55e' : tokens.textMuted,
                    textDecoration: 'underline',
                    textDecorationStyle: 'dotted',
                    textUnderlineOffset: '2px',
                  }}
                >
                  View
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    fontFamily: '"JetBrains Mono", monospace',
                    color: activeAgents > 0 ? '#22c55e' : tokens.textSecondary,
                  }}
                >
                  {Math.min(activeAgents, maxAgents)}/{maxAgents}
                </Typography>
                {queuedAgents > 0 && (
                  <Typography
                    sx={{
                      fontSize: '0.6rem',
                      fontWeight: 700,
                      fontFamily: '"JetBrains Mono", monospace',
                      color: '#f59e0b',
                    }}
                  >
                    +{queuedAgents} queued
                  </Typography>
                )}
              </Stack>
            </Box>
          </Tooltip>
        )}

        {/* Buy More Button - Clean style */}
        <PermissionGuard require={SystemFunction.MANAGE_BILLING}>
          <ButtonBase
            onClick={handleBuyMoreClick}
            sx={{
              mt: 1.5,
              width: '100%',
              py: 0.875,
              px: 1.5,
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.75,
              background: isLight ? 'rgba(99, 102, 241, 0.08)' : 'rgba(99, 102, 241, 0.12)',
              border: `1px solid ${isLight ? 'rgba(99, 102, 241, 0.15)' : 'rgba(99, 102, 241, 0.2)'}`,
              transition: 'all 0.2s ease',
              '&:hover': {
                background: isLight ? 'rgba(99, 102, 241, 0.12)' : 'rgba(99, 102, 241, 0.18)',
                border: `1px solid ${isLight ? 'rgba(99, 102, 241, 0.25)' : 'rgba(99, 102, 241, 0.3)'}`,
              },
            }}
          >
            <IconPlus size={14} style={{ color: '#6366f1' }} />
            <Typography
              sx={{
                fontSize: '0.7rem',
                fontWeight: 600,
                color: '#6366f1',
              }}
            >
              Add More
            </Typography>
          </ButtonBase>
        </PermissionGuard>
      </Box>

      {/* Cancellation Warning */}
      {isScheduledForCancellation && (
        <Box
          sx={{
            mx: 1.5,
            mb: 2,
            borderRadius: '12px',
            background: isLight ? 'rgba(245, 158, 11, 0.06)' : 'rgba(245, 158, 11, 0.08)',
            border: '1px solid rgba(245, 158, 11, 0.2)',
            overflow: 'hidden',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              background: isLight ? 'rgba(245, 158, 11, 0.1)' : 'rgba(245, 158, 11, 0.12)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
            },
          }}
          onClick={() => navigate('/settings?tab=subscription')}
        >
          <Box sx={{ p: 1.75, pb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: isLight ? 'rgba(245, 158, 11, 0.1)' : 'rgba(245, 158, 11, 0.12)',
                  border: '1px solid rgba(245, 158, 11, 0.2)',
                  flexShrink: 0,
                }}
              >
                <IconAlertTriangle size={16} style={{ color: '#f59e0b' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    color: '#f59e0b',
                    lineHeight: 1.3,
                    mb: 0.25,
                  }}
                >
                  Subscription Ending
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.7rem',
                    color: tokens.textSecondary,
                    lineHeight: 1.4,
                  }}
                >
                  Cancels on{' '}
                  <Box component="span" sx={{ fontWeight: 600, color: tokens.textPrimary }}>
                    {new Date(subscription.renewalDate).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </Box>
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              px: 1.75,
              py: 1,
              background: isLight ? 'rgba(245, 158, 11, 0.04)' : 'rgba(245, 158, 11, 0.06)',
              borderTop: '1px solid rgba(245, 158, 11, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.7rem',
                fontWeight: 600,
                color: '#d97706',
              }}
            >
              Reactivate Subscription
            </Typography>
            <IconArrowRight size={14} style={{ color: '#d97706' }} />
          </Box>
        </Box>
      )}

      {/* Running Agents Modal */}
      <RunningAgentsModal
        open={agentsModalOpen}
        onClose={() => setAgentsModalOpen(false)}
        isLight={isLight}
        tokens={tokens}
        activeAgents={activeAgents}
        maxAgents={maxAgents}
        queuedAgents={queuedAgents}
        agents={runningAgents || []}
        agentsLoading={runningAgentsStatus === 'loading'}
        onStop={handleStopAgent}
        onAgentClick={handleAgentClick}
        stoppingAgentId={stoppingAgentId}
      />
    </>
  );
}

export default memo(MenuCard);
